import React, { useEffect, useState, useRef } from "react";
import { usePageType } from "../../hooks/usePageType";
import { Link } from "gatsby";
import AtomIcon from "assets/svg/atom-data.svg";
import Logo from "assets/svg/logo.svg";
import classNames from "classnames";
import { isMobile } from "react-device-detect";
import ArrowRight from "assets/svg/arrow-right.svg";
import BaseButton from "components/button/BaseButton";
import { scroller } from "react-scroll";
import { useScrollPosition } from "../../hooks/useScrollPosition";
import { CSSTransition } from "react-transition-group";

interface Props {
  onToggle: () => void;
  isOpen: boolean;
  location?: Location;
}

const navLinks = [
  { href: "livrables", label: "Nos livrables" },
  { href: "success", label: "Nos succès" },
  { href: "testimonials", label: "Témoignages" },
  { href: "goals", label: "Notre méthode" },
  { href: "team", label: "Notre team d’experts" }
];

const HeaderData = ({ isOpen, onToggle, location }: Props) => {
  const refHeader = useRef(null);
  const [scroll, setScroll] = useState<boolean>(false);
  const [activeSection, setActiveSection] = useState<string>("");
  const [showContactButton, setShowContactButton] = useState<boolean>(false);

  // Refs for mobile menu and buttons
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useScrollPosition(
    ({ currentPosition }) => {
      setScroll(currentPosition.y > 20);
      setShowContactButton(currentPosition.y > 400);

      if (currentPosition.y < 50) {
        setActiveSection("");
        return;
      }

      // Determine active section for the arrow indicators
      for (const sectionId of navLinks.map((link) => link.href)) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          // Consider section active when it's in the viewport with some offset
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    },
    [],
    null,
    true,
    100,
  );

  // Scroll active button into view in mobile menu
  useEffect(() => {
    if (activeSection && mobileMenuRef.current) {
      const activeIndex = navLinks.findIndex(
        (link) => link.href === activeSection,
      );
      const activeButton = buttonRefs.current[activeIndex];

      if (activeButton) {
        activeButton.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [activeSection]);

  const handleClickPlausible = () => {
    if ("plausible" in window) {
      window.plausible("contact-us");
    }
    scroller.scrollTo("intro", {
      duration: 600,
      delay: 0,
      smooth: "easeInOutQuart",
    });
  };

  const handleScrollToSection = (sectionId: string) => {
    scroller.scrollTo(sectionId, {
      duration: 800,
      delay: 0,
      smooth: "easeInOutQuart",
      offset: -80,
    });
  };

  return (
    <header
      ref={refHeader}
      id="js-header"
      data-template="data"
      data-open={isOpen}
      className={classNames(
        "fixed top-0 left-0 w-full z-10 transition-all duration-100 ease-ease",
        {
          "bg-black lg:bg-white": !scroll,
          "bg-black lg:bg-white shadow": scroll,
        },
      )}
    >
      {/* Top header row */}
      <div className="px-4 lg:pl-[100px] lg:pr-4 h-16 lg:h-20 flex items-center justify-between">
        {isOpen ? (
          <span className="text-label2 text-gray-900 font-sporting block lg:hidden">
            MENU
          </span>
        ) : (
          <Link to="/data" className="lg:hidden">
            <div className="flex items-center">
              <Logo className="w-24 mr-2.5" />
              <p className="rounded-5 border border-data-500 inline-flex px-2 py-2 items-center gap-1 uppercase">
                <AtomIcon className="w-3 h-3 shrink-0" />
                <span className="text-data-500 font-bold text-caption2">
                  Data
                </span>
              </p>
            </div>
          </Link>
        )}

        {/* Desktop navigation menu */}
        <div className="hidden lg:flex w-full justify-between items-center">
          <div className="flex gap-x-6">
            {navLinks.map((link, index) => {
              const isActive = activeSection === link.href;

              return (
                <button
                  key={index}
                  onClick={() => handleScrollToSection(link.href)}
                  className={classNames(
                    "text-menu transition-all duration-300 ease-in-out flex items-center gap-2 bg-transparent border-none cursor-pointer relative",
                    {
                      "text-gray-900": isActive,
                      "text-gray-500 hover:text-gray-900": !isActive,
                    },
                  )}
                >
                  <ArrowRight
                    className={classNames(
                      "size-5 text-data-500 transition-all duration-300 ease-in-out absolute",
                      {
                        "opacity-100 left-0": isActive,
                        "opacity-0 -left-9": !isActive,
                      },
                    )}
                  />

                  <span
                    className={classNames(
                      "inline-block transition-all duration-300 ease-in-out",
                      {
                        "ml-8": isActive,
                        "ml-0": !isActive,
                      },
                    )}
                  >
                    {link.label}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Desktop Contact button */}
          <CSSTransition
            in={showContactButton}
            timeout={300}
            classNames="fade"
            unmountOnExit
          >
            <BaseButton
              variant="data"
              onClick={() => handleClickPlausible()}
              role="button"
              className="flex items-center gap-2"
              ctaName="contact"
              size="md"
            >
              Nous contacter
              <ArrowRight className="size-6" />
            </BaseButton>
          </CSSTransition>
        </div>

        {/* Mobile Contact button */}
        <div className="flex lg:hidden items-center">
          <CSSTransition
            in={showContactButton}
            timeout={300}
            classNames="fade"
            unmountOnExit
          >
            <BaseButton
              variant="data"
              onClick={() => handleClickPlausible()}
              size="md"
              role="button"
              className="flex items-center gap-2"
              ctaName="contact-mobile"
            >
              Contact
              <ArrowRight className="size-5" />
            </BaseButton>
          </CSSTransition>
        </div>
      </div>

      {/* Mobile horizontal menu - visible below header */}
      <div
        ref={mobileMenuRef}
        className="lg:hidden w-full overflow-x-auto border-t border-gray-800 scrollbar-hide bg-white"
      >
        <div className="flex gap-4 px-3 py-2 min-w-max">
          {navLinks.map((link, index) => {
            const isActive = activeSection === link.href;

            return (
              <button
                key={index}
                ref={(el) => (buttonRefs.current[index] = el)}
                onClick={() => handleScrollToSection(link.href)}
                className={classNames(
                  "text-menu transition-all duration-300 ease-in-out flex items-center gap-2 bg-transparent border-none cursor-pointer whitespace-nowrap",
                  {
                    "text-gray-900": isActive,
                    "text-gray-500 hover:text-gray-900": !isActive,
                  },
                )}
              >
                <ArrowRight
                  className={classNames(
                    "size-4 text-data-500 transition-all duration-300 ease-in-out",
                    {
                      "opacity-100": isActive,
                      "opacity-0": !isActive,
                    },
                  )}
                />
                <span>{link.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </header>
  );
};

export default HeaderData;
